// public/js/modules/demo-mode.js
// The sidebar "Demo" switch: one click from wherever you are into the demo
// tenant and its map (docs/demo-tenant.md).
//
// This REPLACED an earlier demo mode that blurred tenant names so the platform
// could be shown over screen-share without exposing customer identities.
// Switching into a tenant whose data is invented is a better answer to the same
// problem than hiding the data of one that is real - so the blur is gone, and
// `tName()` survives only as the escaping wrapper its call sites still use.
//
// The switch has NO STATE OF ITS OWN. It is on when the active tenant scope is
// a demo tenant, off otherwise. A stored "demo mode" flag would be a second
// source of truth that disagrees with the scope exactly when someone changes
// tenant through the normal selector - the moment the UI must not lie.
import { escapeHtml } from '../utils/escape.js';
import { getMemberships, getScope, setScope } from './tenant-scope.js';

const LAST_KEY = 'superb_scope_before_demo';

/** Wrap a tenant name for rendering. Kept because call sites use it; no longer
 *  blurs anything. */
export function tName(name) {
  const s = (name == null || name === '') ? '—' : String(name);
  return `<span class="tenant-name">${escapeHtml(s)}</span>`;
}

/** The demo tenants this user belongs to, in membership order. */
export function demoTenants() {
  return getMemberships().filter(t => t.isDemo === true);
}

/** Is the active scope a demo tenant? This is the switch's state. */
export function isDemoScope() {
  const s = getScope();
  if (!s || s.all || s.unassigned || !s.tenantId) return false;
  return demoTenants().some(t => t.tenantId === s.tenantId);
}

function enter() {
  const target = demoTenants()[0];
  if (!target) return;
  // Remember where we were so Off can put it back.
  try {
    const cur = getScope();
    if (!isDemoScope()) localStorage.setItem(LAST_KEY, JSON.stringify(cur));
  } catch {}
  setScope({ tenantId: target.tenantId });
  // A demo starts on the map. This jump is specific to THIS switch - changing
  // customer through the normal selector while reading an asset should keep you
  // on that asset.
  window.location.hash = '#map';
}

function leave() {
  let prev = null;
  try { prev = JSON.parse(localStorage.getItem(LAST_KEY) || 'null'); } catch {}
  try { localStorage.removeItem(LAST_KEY); } catch {}
  // Fall back to "all tenants" rather than staying in the demo if we have
  // nothing to restore.
  setScope(prev && (prev.tenantId || prev.all || prev.unassigned) ? prev : { all: true });
}

/** Reflect the current scope on the switch and the body class. */
export function refreshDemoSwitch() {
  const on = isDemoScope();
  document.documentElement.classList.toggle('demo-scope', on);
  const input = document.getElementById('demo-mode-input');
  if (input) input.checked = on;
}

export function initDemoMode() {
  // Only people who actually have a demo tenant see the switch. For every
  // customer there is no toggle, no label and no hint that one exists.
  if (!demoTenants().length) return;
  if (document.getElementById('demo-mode-input')) { refreshDemoSwitch(); return; }

  const host = document.querySelector('.sidebar-footer') || document.body;
  const wrap = document.createElement('div');
  wrap.className = 'demo-switch text-center';
  wrap.innerHTML =
    '<div class="form-check form-switch d-inline-flex align-items-center gap-1 m-0">'
    + '<input class="form-check-input" type="checkbox" role="switch" id="demo-mode-input">'
    + '<label class="form-check-label small" for="demo-mode-input" '
    + 'title="Switch into the demo tenant and open the map">Demo</label>'
    + '</div>';
  host.appendChild(wrap);

  wrap.querySelector('#demo-mode-input').addEventListener('change', (e) => {
    if (e.target.checked) enter(); else leave();
    refreshDemoSwitch();
  });
  refreshDemoSwitch();
}
